import React, { useState } from 'react';
import { Mail, Phone, MapPin, Send, CalendarCheck, CheckCircle2 } from 'lucide-react';
import { SEO } from '../components/SEO';
const COUNTRIES = ['United States', 'Canada', 'United Kingdom', 'Australia', 'India', 'Germany', 'France', 'Singapore', 'United Arab Emirates', 'Other'];
export function Contact() {
  const [submitted, setSubmitted] = useState(false);
  const [intent, setIntent] = useState<'quote' | 'consultation'>('quote');
  const handleSubmit = (e: React.FormEvent, type: 'quote' | 'consultation') => {
    e.preventDefault();
    setIntent(type);
    setSubmitted(true);
    setTimeout(() => setSubmitted(false), 4000);
  };
  return <div className="w-full pt-20 pb-24 bg-slate-50 dark:bg-navy-900/50 min-h-screen" data-id="element-233">
      <SEO title="Contact Hirelio | Request a Resume Writing Quote" description="Get in touch with Hirelio Career Services. Request a personalized quote or schedule a free consultation with a professional resume writer." data-id="element-234" />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-20" data-id="element-235">
        <div className="grid lg:grid-cols-2 gap-16" data-id="element-236">
          {/* Contact Info */}
          <div data-id="element-237">
            <h1 className="text-4xl md:text-5xl font-heading font-bold text-navy-900 dark:text-white mb-6" data-id="element-238">
              Get in Touch
            </h1>
            <p className="text-lg text-slate-600 dark:text-slate-400 mb-12 leading-relaxed" data-id="element-239">
              Have questions about our services or need help choosing the right
              package? Request a personalized quote or schedule a free
              consultation with a career expert.
            </p>

            <div className="space-y-8" data-id="element-240">
              <div className="flex items-start gap-4" data-id="element-241">
                <div className="w-12 h-12 bg-white dark:bg-navy-800 rounded-xl flex items-center justify-center text-brand shrink-0 shadow-sm border border-slate-100 dark:border-navy-700" data-id="element-242">
                  <Mail size={24} data-id="element-243" />
                </div>
                <div data-id="element-244">
                  <h3 className="font-heading font-bold text-navy-900 dark:text-white text-lg mb-1" data-id="element-245">
                    Email Us
                  </h3>
                  <p className="text-slate-600 dark:text-slate-400 mb-1" data-id="element-246">
                    We aim to respond within 24 hours.
                  </p>
                  <a href="mailto:Hello@hireliocareerservices.com" className="text-brand font-medium hover:underline" data-id="element-247">
                    Hello@hireliocareerservices.com
                  </a>
                </div>
              </div>

              <div className="flex items-start gap-4" data-id="element-248">
                <div className="w-12 h-12 bg-white dark:bg-navy-800 rounded-xl flex items-center justify-center text-brand shrink-0 shadow-sm border border-slate-100 dark:border-navy-700" data-id="element-249">
                  <Phone size={24} data-id="element-250" />
                </div>
                <div data-id="element-251">
                  <h3 className="font-heading font-bold text-navy-900 dark:text-white text-lg mb-1" data-id="element-252">
                    Call Us
                  </h3>
                  <p className="text-slate-600 dark:text-slate-400 mb-1" data-id="element-253">
                    Mon-Fri from 9am to 6pm EST.
                  </p>
                  <a href="tel:+18005550199" className="text-brand font-medium hover:underline" data-id="element-254">
                    +1 (800) 555-0199
                  </a>
                </div>
              </div>

              <div className="flex items-start gap-4" data-id="element-255">
                <div className="w-12 h-12 bg-white dark:bg-navy-800 rounded-xl flex items-center justify-center text-brand shrink-0 shadow-sm border border-slate-100 dark:border-navy-700" data-id="element-256">
                  <MapPin size={24} data-id="element-257" />
                </div>
                <div data-id="element-258">
                  <h3 className="font-heading font-bold text-navy-900 dark:text-white text-lg mb-1" data-id="element-259">
                    Office
                  </h3>
                  <p className="text-slate-600 dark:text-slate-400" data-id="element-260">
                    100 Career Avenue, Suite 400
                    <br data-id="element-261" />
                    New York, NY 10001
                  </p>
                </div>
              </div>
            </div>

            <div className="mt-12 flex items-center gap-3 text-sm text-slate-500 dark:text-slate-400" data-id="element-262">
              <CheckCircle2 size={18} className="text-emerald-500 shrink-0" data-id="element-263" />
              Your information is encrypted and never shared with third parties.
            </div>
          </div>

          {/* Contact Form */}
          <div className="bg-white dark:bg-navy-800 rounded-3xl p-8 md:p-10 shadow-xl border border-slate-100 dark:border-navy-700" data-id="element-264">
            {submitted ? <div className="h-full flex flex-col items-center justify-center text-center py-12 animate-in fade-in zoom-in duration-300" data-id="element-265">
                <div className="w-20 h-20 bg-emerald-100 dark:bg-emerald-900/30 text-emerald-500 rounded-full flex items-center justify-center mb-6" data-id="element-266">
                  <CheckCircle2 size={40} data-id="element-267" />
                </div>
                <h3 className="text-2xl font-heading font-bold text-navy-900 dark:text-white mb-2" data-id="element-268">
                  {intent === 'quote' ? 'Quote Request Received!' : 'Consultation Requested!'}
                </h3>
                <p className="text-slate-600 dark:text-slate-400 max-w-sm" data-id="element-269">
                  {intent === 'quote' ? 'Thanks for reaching out. A career specialist will email your personalized quote within 24 hours.' : 'Thanks! We will reach out shortly to confirm a time for your free consultation.'}
                </p>
              </div> : <form className="space-y-5" data-id="element-270">
                <div data-id="element-271">
                  <label htmlFor="contact-name" className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2" data-id="element-272">
                    Name
                  </label>
                  <input id="contact-name" required type="text" autoComplete="name" className="w-full px-4 py-3 rounded-xl border border-slate-200 dark:border-navy-600 bg-slate-50 dark:bg-navy-900 text-slate-900 dark:text-white focus:ring-2 focus:ring-brand/50 focus:border-brand outline-none transition-all" data-id="element-273" />
                </div>

                <div className="grid md:grid-cols-2 gap-5" data-id="element-274">
                  <div data-id="element-275">
                    <label htmlFor="contact-email" className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2" data-id="element-276">
                      Email
                    </label>
                    <input id="contact-email" required type="email" autoComplete="email" className="w-full px-4 py-3 rounded-xl border border-slate-200 dark:border-navy-600 bg-slate-50 dark:bg-navy-900 text-slate-900 dark:text-white focus:ring-2 focus:ring-brand/50 focus:border-brand outline-none transition-all" data-id="element-277" />
                  </div>
                  <div data-id="element-278">
                    <label htmlFor="contact-phone" className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2" data-id="element-279">
                      Phone
                    </label>
                    <input id="contact-phone" type="tel" autoComplete="tel" className="w-full px-4 py-3 rounded-xl border border-slate-200 dark:border-navy-600 bg-slate-50 dark:bg-navy-900 text-slate-900 dark:text-white focus:ring-2 focus:ring-brand/50 focus:border-brand outline-none transition-all" data-id="element-280" />
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-5" data-id="element-281">
                  <div data-id="element-282">
                    <label htmlFor="contact-country" className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2" data-id="element-283">
                      Country
                    </label>
                    <select id="contact-country" className="w-full px-4 py-3 rounded-xl border border-slate-200 dark:border-navy-600 bg-slate-50 dark:bg-navy-900 text-slate-900 dark:text-white focus:ring-2 focus:ring-brand/50 focus:border-brand outline-none transition-all" data-id="element-284">
                      {COUNTRIES.map(c => <option key={c} data-id="element-285">{c}</option>)}
                    </select>
                  </div>
                  <div data-id="element-286">
                    <label htmlFor="contact-title" className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2" data-id="element-287">
                      Current Job Title
                    </label>
                    <input id="contact-title" type="text" autoComplete="organization-title" className="w-full px-4 py-3 rounded-xl border border-slate-200 dark:border-navy-600 bg-slate-50 dark:bg-navy-900 text-slate-900 dark:text-white focus:ring-2 focus:ring-brand/50 focus:border-brand outline-none transition-all" data-id="element-288" />
                  </div>
                </div>

                <div data-id="element-289">
                  <label htmlFor="contact-message" className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2" data-id="element-290">
                    Message
                  </label>
                  <textarea id="contact-message" required rows={4} placeholder="Tell us about your career goals and which services you're interested in..." className="w-full px-4 py-3 rounded-xl border border-slate-200 dark:border-navy-600 bg-slate-50 dark:bg-navy-900 text-slate-900 dark:text-white focus:ring-2 focus:ring-brand/50 focus:border-brand outline-none transition-all resize-none placeholder:text-slate-400" data-id="element-291"></textarea>
                </div>

                <div className="flex flex-col sm:flex-row gap-3 pt-1" data-id="element-292">
                  <button type="submit" onClick={e => handleSubmit(e, 'quote')} className="flex-1 bg-brand hover:bg-brand-600 text-white py-4 rounded-xl font-medium transition-all shadow-md hover:shadow-lg flex items-center justify-center gap-2" data-id="element-293">
                    Request Quote <Send size={18} data-id="element-294" />
                  </button>
                  <button type="submit" onClick={e => handleSubmit(e, 'consultation')} className="flex-1 bg-slate-100 dark:bg-navy-700 hover:bg-slate-200 dark:hover:bg-navy-600 text-navy-900 dark:text-white py-4 rounded-xl font-medium transition-all flex items-center justify-center gap-2" data-id="element-295">
                    Schedule Consultation <CalendarCheck size={18} data-id="element-296" />
                  </button>
                </div>
              </form>}
          </div>
        </div>
      </div>
    </div>;
}